import { Navigate, useParams } from 'react-router-dom'
import { WithAuthProvider } from '@/contexts/auth/auth'
import { QuestionnaireWrapper } from '@/components/questionnaireWrapper'
import { useQuestionnaireSession } from '@/hooks/useQuestionnaireSession'
import { Helmet } from 'react-helmet'
import { Loader } from '@/components/loader'
import { useEffect, useState } from 'react'
import { SubmitStateProvider } from '@/contexts/submitState/submitState'
import { EmbedProvider } from '@/contexts/embed/embed'
import ResizeObserver from 'resize-observer-polyfill'

export interface EmbeddedQuestionnairePageProps {
  authToken: string
}

export function EmbeddedQuestionnairePage({
  authToken
}: EmbeddedQuestionnairePageProps) {
  const { questionnaireId } = useParams()
  const [error, setError] = useState<unknown>()

  const {
    error: sessionError,
    loading,
    data,
    maintenanceMode,
    approvalWorkflow
  } = useQuestionnaireSession({
    questionnaireId: questionnaireId ?? '',
    authToken
  })

  useEffect(() => {
    if (error || sessionError) {
      const e = error ?? sessionError
      const message =
        e instanceof Error ? e.message : typeof e === 'string' ? e : 'Error'

      window.parent.postMessage(
        {
          type: 'error',
          message
        },
        '*' // allowing any target domain because we don't send sensitive data
      )
    }
  }, [error, sessionError])

  useEffect(() => {
    // using lastResize to dedupe resize events
    // we don't need the state to be accessible in React so we can use a variable + closure instead of a useState
    // this is useful as there's a delay with useState which could allow duplicate events to be sent while it updates
    let lastResize = 0

    // Radix popovers (the clinical select, multi-select, and date/time pickers)
    // render their dropdown in a portal positioned `fixed`, so it never extends
    // document.body.scrollHeight and the host iframe won't grow to fit it the way
    // react-select's in-flow menu does. We measure any open popper explicitly and
    // include its bottom edge so the iframe grows to reveal the options.
    const POPPER_SELECTOR = '[data-radix-popper-content-wrapper]'

    function computeHeight() {
      let bottom = document.body.scrollHeight

      document.querySelectorAll(POPPER_SELECTOR).forEach(popper => {
        const rect = popper.getBoundingClientRect()
        bottom = Math.max(bottom, Math.ceil(rect.bottom + window.scrollY))
      })

      // adding 1 extra pixel helps avoid rounding issues
      return bottom + 1
    }

    function postResize() {
      const resizeHeight = computeHeight()

      if (resizeHeight === lastResize) return
      lastResize = resizeHeight

      window.parent.postMessage(
        {
          type: 'resize',
          height: resizeHeight
        },
        '*' // allowing any target domain because we don't send sensitive data
      )
    }

    const resizeObserver = new ResizeObserver(postResize)
    resizeObserver.observe(document.body)

    // A fixed popover opening doesn't resize document.body, so the ResizeObserver
    // above won't fire. Watch for popper wrappers being added/removed and observe
    // each one (its height changes as the user filters the list) so we recompute.
    const observedPoppers = new Set<Element>()

    function syncPoppers() {
      const poppers = new Set(document.querySelectorAll(POPPER_SELECTOR))

      poppers.forEach(popper => {
        if (!observedPoppers.has(popper)) {
          resizeObserver.observe(popper)
          observedPoppers.add(popper)
        }
      })

      observedPoppers.forEach(popper => {
        if (!poppers.has(popper)) {
          resizeObserver.unobserve(popper)
          observedPoppers.delete(popper)
        }
      })

      postResize()
    }

    const mutationObserver = new MutationObserver(syncPoppers)
    mutationObserver.observe(document.body, { childList: true })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [])

  if (loading) return <Loader message='Loading' />
  if (sessionError ?? error) throw sessionError ?? error
  if (maintenanceMode) return <Navigate to='/embed/maintenance' />

  function handleSubmit() {
    window.parent.postMessage(
      { type: 'submit' },
      '*' // allowing any target domain because we don't send sensitive data
    )
  }

  return (
    <>
      <Helmet>
        <title>{data.questionnaire.title}</title>
        <meta name='description' content={data.questionnaire.description} />
        <meta property='og:title' content={data.questionnaire.title} />
        <meta
          property='og:description'
          content={data.questionnaire.description}
        />
      </Helmet>
      <SubmitStateProvider>
        <EmbedProvider questionnaireId={questionnaireId ?? ''}>
          <QuestionnaireWrapper
            data={data}
            onError={setError}
            onSubmit={handleSubmit}
            approvalWorkflow={approvalWorkflow}
          />
        </EmbedProvider>
      </SubmitStateProvider>
    </>
  )
}

export default WithAuthProvider(EmbeddedQuestionnairePage)
